import React from 'react';
import styled from '@emotion/styled';

import { ProgressBar } from './ProgressBar';
import { StudentScheduleSlot, type SubjectMetrics, type SubjectAverageMetrics } from '../model';
import {
  getTimeRemainingMessage,
  isNonLearningSlot,
  getAverageTimeSpentMessage,
  isAverageStatsVisible,
} from './utils';
import { getPercetageValue } from '../utils';
import { isSubjectMastered } from './SlotsRing/slotRingUtils';

const Container = styled.div`
  position: absolute;
  inset: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 50px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 8px;
`;

const ProgressContianer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 400px;
  max-width: 400px;
`;

const Heading = styled.span`
  color: #0f1629;
  font-size: 20px;
  font-weight: 700;
`;

const TimeLabel = styled.span`
  color: #686666;
  font-size: 16px;
  font-weight: 600;
`;

const InfoText = styled.span`
  color: #3169f6;
  font-size: 14px;
  font-weight: 400;
`;

type Props = {
  selectedSubjectStats: SubjectMetrics | null | undefined;
  selectedSubjectTimeSpent: [number, number];
  selectedSubject: StudentScheduleSlot | null;
  nextSubject: StudentScheduleSlot | null;
  currentTime: Date;
  averageDailyStats: SubjectAverageMetrics;
};

export const SubjectStats = ({
  currentTime,
  selectedSubjectStats,
  selectedSubjectTimeSpent,
  selectedSubject,
  nextSubject,
  averageDailyStats,
}: Props) => {
  const { slotLabel, repeatedClass } = selectedSubject ?? {};
  const { accuracy, timeSpent, mastery } = selectedSubjectStats ?? {};
  const nextOrCurrentSubject = isNonLearningSlot(slotLabel) ? nextSubject : selectedSubject;

  const timeRemainingMessage = getTimeRemainingMessage(
    currentTime,
    nextOrCurrentSubject?.startTime,
    nextOrCurrentSubject?.endTime
  );

  const timeSpentLearning = repeatedClass
    ? selectedSubjectTimeSpent[1]
    : selectedSubjectTimeSpent[0];
  const timeSpentPercentage = getPercetageValue(timeSpent?.target ?? 0, timeSpentLearning);

  const isMastered = isSubjectMastered(selectedSubjectStats);
  const showDailyAverage = isAverageStatsVisible(currentTime, isMastered, selectedSubject);
  const averageTimeSpentMessage = getAverageTimeSpentMessage(currentTime);

  return (
    <Container>
      <Content>
        <TextContainer>
          <Heading>{!showDailyAverage ? nextOrCurrentSubject?.slotLabel : 'My Day Today'}</Heading>
          {!showDailyAverage && (
            <TimeLabel>
              {nextOrCurrentSubject?.startTimeLabel} - {nextOrCurrentSubject?.endTimeLabel}
            </TimeLabel>
          )}
          <InfoText>{showDailyAverage ? averageTimeSpentMessage : timeRemainingMessage}</InfoText>
        </TextContainer>
        {!showDailyAverage ? (
          <ProgressContianer>
            <ProgressBar
              type="neuroboost"
              label="Mastered Levels"
              progress={mastery?.completedPercentage ?? 0}
              progressValue={Math.floor(mastery?.actual ?? 0)}
              goalValue={Math.floor(mastery?.target ?? 1)}
              animationDelay={200}
            />
            <ProgressBar
              type="accuracy"
              label="Accuracy"
              progress={accuracy?.completedPercentage ?? 0}
              progressValue={Math.floor(accuracy?.actual ?? 0)}
              goalValue={`${Math.floor(accuracy?.target ?? 80)}+`}
              animationDelay={100}
            />
            <ProgressBar
              type="timespent"
              label="Time Spent"
              progress={timeSpentPercentage}
              progressValue={`${Math.floor(timeSpentLearning ?? 0)} min`}
              goalValue={`${Math.floor(timeSpent?.target ?? 25)} min`}
              animationDelay={0}
            />
          </ProgressContianer>
        ) : (
          <ProgressContianer>
            <ProgressBar
              type="neuroboost"
              label="Total Mastered Levels"
              progress={averageDailyStats?.mastery?.completedPercentage ?? 0}
              progressValue={Math.floor(averageDailyStats?.mastery?.actual ?? 0)}
              goalValue={Math.floor(averageDailyStats?.mastery?.target ?? 1)}
              animationDelay={200}
            />
            <ProgressBar
              type="accuracy"
              label="Average Accuracy"
              progress={averageDailyStats?.accuracy?.completedPercentage ?? 0}
              progressValue={Math.floor(averageDailyStats?.accuracy?.actual ?? 0)}
              goalValue={Math.floor(averageDailyStats?.accuracy?.target ?? 80)}
              animationDelay={100}
            />
          </ProgressContianer>
        )}
      </Content>
    </Container>
  );
};
